class ModelSwitcher extends HTMLElement {
  constructor() {
    super();

    this.tabby = null;

    this.animationDurationMs = 200;
    this.panelTimeouts = new WeakMap();
  }

  connectedCallback() {
    void this.initModelTabs();
  }

  disconnectedCallback() {
    this.tabby?.destroy?.();
    this.tabby = null;
  }

  async initModelTabs() {
    const tabsElement = this.querySelector('[data-tabs="model-switcher-tabs"]');

    if (!tabsElement || this.tabby) return;

    const tabbySelector = tabsElement.id ? `#${tabsElement.id}` : '[data-tabs="model-switcher-tabs"]';

    try {
      const { default: Tabby } = await import('tabbyjs');

      this.tabby = new Tabby(tabbySelector);

      this.preparePanelAnimations();

      const setActiveClass = () => {
        tabsElement.querySelectorAll('.active').forEach((el) => el.classList.remove('active'));
        tabsElement.querySelector('[aria-selected="true"]')?.classList.add('active');
      };

      setActiveClass();

      tabsElement.addEventListener('tabby', (event) => {
        setActiveClass();
        this.animatePanelsOnTabChange(event);
        this.scrollToOpenedPanel(event);
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ModelSwitcher] Failed to initialize Tabby:', error);
    }
  }

  prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  }

  isMobileViewport() {
    return window.matchMedia?.('(max-width: 767px)')?.matches ?? false;
  }

  scrollToOpenedPanel(event) {
    if (!this.isMobileViewport()) return;

    const panel = event?.detail?.content;

    if (!(panel instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      panel.scrollIntoView({
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }

  preparePanelAnimations() {
    if (this.prefersReducedMotion()) return;

    const panels = this.querySelectorAll('[role="tabpanel"], [id^="panel-"]');

    panels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;

      panel.style.transition = [
        `opacity ${this.animationDurationMs}ms ease-in-out`,
        `transform ${this.animationDurationMs}ms ease-in-out`,
      ].join(', ');
      panel.style.willChange = 'opacity, transform';

      if (panel.hasAttribute('hidden')) {
        panel.style.opacity = '0';
        panel.setAttribute('aria-hidden', 'true');
      } else {
        panel.style.opacity = '1';
        panel.style.transform = 'translateY(0px)';
        panel.setAttribute('aria-hidden', 'false');
      }
    });
  }

  clearPanelTimeout(panel) {
    const timeoutId = this.panelTimeouts.get(panel);

    if (timeoutId) window.clearTimeout(timeoutId);

    this.panelTimeouts.delete(panel);
  }

  animatePanelsOnTabChange(event) {
    if (this.prefersReducedMotion()) return;

    const { previousContent, content } = event?.detail ?? {};

    if (!(content instanceof HTMLElement)) return;

    // Fade in the newly activated panel
    this.clearPanelTimeout(content);
    content.removeAttribute('hidden');
    content.setAttribute('aria-hidden', 'false');
    content.style.opacity = '0';
    content.style.zIndex = '0';

    requestAnimationFrame(() => {
      content.style.opacity = '1';
      content.style.transform = 'translateY(0px)';
    });

    // Fade out the previous panel, then re-apply [hidden]
    if (!(previousContent instanceof HTMLElement)) return;

    this.clearPanelTimeout(previousContent);

    // Tabby has already applied [hidden] to previousContent before emitting the event.
    // Temporarily unhide it, overlay it, animate out, then hide again.
    previousContent.removeAttribute('hidden');
    previousContent.setAttribute('aria-hidden', 'true');
    previousContent.style.position = 'absolute';
    previousContent.style.inset = '0';
    previousContent.style.zIndex = '1';
    previousContent.style.opacity = '1';
    previousContent.style.transform = 'translateY(0px)';

    requestAnimationFrame(() => {
      previousContent.style.opacity = '0';
    });

    const timeoutId = window.setTimeout(() => {
      previousContent.setAttribute('hidden', 'hidden');
      previousContent.style.position = '';
      previousContent.style.inset = '';
      previousContent.style.zIndex = '';
    }, this.animationDurationMs);

    this.panelTimeouts.set(previousContent, timeoutId);
  }
}

customElements.define('model-switcher', ModelSwitcher);
